import { useState, useEffect } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon } from 'lucide-react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/pages/dashboard/admin/tenants/[slug]/form';
import { collectionsApi } from '@/lib/api/collections';
import { ApiRequestError } from '@/lib/api/client';
import { CollectionDataPanel } from '@/components/shared/collection-data-panel';
import type { Collection, CollectionCreateInput, CollectionUpdateInput, CollectionWithDetails, ResourceOperation, Tenant } from '@/types/entities';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

type SelectedCollection = CollectionWithDetails & {
  rawDetails: string;
};

type DestructiveUpdateError = {
  requiresForce?: boolean;
  warnings?: string[];
};

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

type CollectionsManagementPanelProps = {
  tenant: Tenant;
  initialCollectionName?: string;
  onCollectionSelect?: (collectionName: string | null) => void;
  dataEditorMode?: 'create' | 'edit' | null;
  dataEditorItemId?: number | null;
  onDataEditorModeChange?: (mode: 'create' | 'edit' | null, itemId?: number | null) => void;
}

export function CollectionsManagementPanel({ 
  tenant, 
  initialCollectionName,
  onCollectionSelect,
  dataEditorMode = null,
  dataEditorItemId = null,
  onDataEditorModeChange,
}: CollectionsManagementPanelProps) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<SelectedCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<CollectionWithDetails | undefined>();
  const [collectionToDelete, setCollectionToDelete] = useState<Collection | null>(null);
  const [pendingForceUpdate, setPendingForceUpdate] = useState<CollectionUpdateInput | null>(null);
  const [destructiveWarnings, setDestructiveWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const getAllowedOps = (details: CollectionWithDetails['details']): ResourceOperation[] => {
    const configuredOps = details.ops?.length ? details.ops : ALL_OPERATIONS;
    const uniqueOps = Array.from(new Set<ResourceOperation>(configuredOps));
    return ALL_OPERATIONS.filter((operation) => uniqueOps.includes(operation));
  };

  const fetchCollections = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await collectionsApi.getAllForTenant(tenant.slug);
      setCollections(data);
      
      // If there's an initialCollectionName, try to select it
      if (initialCollectionName && !selectedCollection) {
        const targetCollection = data.find(c => c.name === initialCollectionName);
        if (targetCollection) {
          const fullCollection = await collectionsApi.getById(tenant.slug, targetCollection.id);
          const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
          setSelectedCollection({ ...fullCollection, details, rawDetails: fullCollection.details });
          onCollectionSelect?.(targetCollection.name);
        }
      } else if (data.length > 0 && !selectedCollection && !initialCollectionName) {
        // Auto-select the first collection if no initialCollectionName is provided
        const fullCollection = await collectionsApi.getById(tenant.slug, data[0].id);
        const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
        setSelectedCollection({ ...fullCollection, details, rawDetails: fullCollection.details });
        onCollectionSelect?.(data[0].name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSelectedCollection = async (collectionId: number) => {
    const fullCollection = await collectionsApi.getById(tenant.slug, collectionId);
    const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
    setSelectedCollection({ ...fullCollection, details, rawDetails: fullCollection.details });
    onCollectionSelect?.(fullCollection.name);
  };

  useEffect(() => {
    fetchCollections();
  }, [tenant.slug]);

  // Handle initialCollectionName changes (e.g., when query param changes)
  useEffect(() => {
    if (initialCollectionName && collections.length > 0) {
      const targetCollection = collections.find(c => c.name === initialCollectionName);
      if (targetCollection && selectedCollection?.name !== initialCollectionName) {
        handleSelect(targetCollection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCollectionName, collections]);

  const handleCreate = () => {
    setEditingCollection(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = async (collection: Collection) => {
    try {
      const fullCollection = await collectionsApi.getById(tenant.slug, collection.id);
      const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
      setEditingCollection({ ...fullCollection, details });
      setIsDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    }
  };


  const handleSelect = async (collection: Collection) => {
    try {
      const fullCollection = await collectionsApi.getById(tenant.slug, collection.id);
      const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
      setSelectedCollection({ ...fullCollection, details, rawDetails: fullCollection.details });
      onCollectionSelect?.(collection.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    }
  };

  const handleDelete = (collection: Collection) => {
    setCollectionToDelete(collection);
  };

  const confirmDelete = async () => {
    if (!collectionToDelete) return;

    try {
      await collectionsApi.delete(tenant.slug, collectionToDelete.id);
      if (selectedCollection?.id === collectionToDelete.id) {
        setSelectedCollection(null);
      }
      setCollectionToDelete(null);
      fetchCollections();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete collection');
    }
  };

  const handleSubmit = async (data: CollectionCreateInput | CollectionUpdateInput) => {
    if (editingCollection) {
      try {
        await collectionsApi.update(tenant.slug, editingCollection.id, data as CollectionUpdateInput);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 409) {
          const errorData = err.data as DestructiveUpdateError | undefined;
          if (errorData?.requiresForce) {
            setPendingForceUpdate(data as CollectionUpdateInput);
            setDestructiveWarnings(errorData.warnings ?? []);
            return;
          }
        }
        throw err;
      }
      await fetchCollections();
      if (selectedCollection?.id === editingCollection.id) {
        await refreshSelectedCollection(editingCollection.id);
      }
    } else {
      await collectionsApi.create(tenant.slug, data as CollectionCreateInput);
      await fetchCollections();
    }
    setIsDialogOpen(false);
    setEditingCollection(undefined);
  };

  const confirmForceUpdate = async () => {
    if (!editingCollection || !pendingForceUpdate) {
      return;
    }

    try {
      await collectionsApi.update(tenant.slug, editingCollection.id, {
        ...pendingForceUpdate,
        force: true,
      });
      await fetchCollections();
      setPendingForceUpdate(null);
      setDestructiveWarnings([]);
      setIsDialogOpen(false);
      setEditingCollection(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply destructive update');
    }
  };

  const getColumnCount = (collection: Collection) => {
    try {
      const details = JSON.parse(collection.details) as CollectionWithDetails['details'];
      return details.columns.length;
    } catch {
      return 0;
    }
  };

  const getCollectionOps = (collection: Collection): ResourceOperation[] => {
    try {
      const details = JSON.parse(collection.details) as CollectionWithDetails['details'];
      return getAllowedOps(details);
    } catch {
      return ALL_OPERATIONS;
    }
  };

  const handleCopyDetails = async (collection: Collection) => {
    try {
      const fullCollection = await collectionsApi.getById(tenant.slug, collection.id);
      await navigator.clipboard.writeText(fullCollection.details);
    } catch {
      setError('Failed to copy collection details JSON');
    }
  };

  return (
    <>
      <ResizablePanelGroup direction="horizontal" className="border rounded-lg overflow-hidden">
      {/* Left Panel: Collections List */}
      <ResizablePanel defaultSize={40} minSize={30} className="h-[calc(100vh-13rem)] min-h-[600px] bg-muted/20">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b bg-background">
            <div>
              <h3 className="font-semibold">Collections</h3>
              <p className="text-xs text-muted-foreground">{collections.length} collections</p>
            </div>
            <Button size="sm" onClick={handleCreate} className="gap-1">
              <PlusIcon className="size-4" />
              New
            </Button>
          </div>

          {error && (
            <div className="p-4 text-destructive text-sm">{error}</div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center flex-1">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : collections.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center p-8">
              <div className="text-muted-foreground mb-2">No collections yet</div>
              <p className="text-sm text-muted-foreground mb-4">Create your first collection to get started</p>
              <Button size="sm" onClick={handleCreate} className="gap-1">
                <PlusIcon className="size-4" />
                Create Collection
              </Button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {collections.map((collection) => (
                <div
                  key={collection.id}
                  className={cn(
                    "p-4 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50",
                    selectedCollection?.id === collection.id && "bg-muted"
                  )}
                  onClick={() => handleSelect(collection)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{collection.label || collection.name}</span>
                      </div>
                      {collection.label && collection.label !== collection.name && (
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="font-mono text-xs">
                            {collection.name}
                          </Badge>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {getColumnCount(collection)} columns
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {getCollectionOps(collection).map((operation) => (
                          <Badge key={`${collection.id}-${operation}`} variant="outline" className="text-[10px] capitalize">
                            {operation}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleCopyDetails(collection)}
                        title="Copy Collection JSON"
                      >
                        <CopyIcon className="size-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEdit(collection)}
                        title="Edit schema"
                      >
                        <EditIcon className="size-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(collection)}
                        title="Delete"
                      >
                        <TrashIcon className="size-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Right Panel: Collection Data */}
      <ResizablePanel defaultSize={60} minSize={40} className="h-[calc(100vh-13rem)] min-h-[600px] bg-background overflow-hidden">
        {selectedCollection ? (
          <div className="h-full overflow-y-auto p-4">
            <CollectionDataPanel
              tenant={{
                id: tenant.id,
                slug: tenant.slug,
                name: tenant.name,
                websiteUrl: tenant.website_url,
                collections: collections,
              }}
              collection={selectedCollection}
              onCollectionChange={fetchCollections}
              editorMode={dataEditorMode}
              editingItemId={dataEditorItemId}
              onCreateRoute={() => onDataEditorModeChange?.('create')}
              onEditRoute={(itemId) => onDataEditorModeChange?.('edit', itemId)}
              onExitEditorRoute={() => onDataEditorModeChange?.(null)}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="text-muted-foreground mb-2">Select a collection</div>
            <p className="text-sm text-muted-foreground">
              Choose a collection from the left panel to view and manage its data
            </p>
          </div>
        )}
      </ResizablePanel>

      <Form
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingCollection(undefined);
          setPendingForceUpdate(null);
          setDestructiveWarnings([]);
        }}
        collection={editingCollection}
        title="Collection"
        description={editingCollection ? 'Update the schema for this collection.' : 'Define the schema for this collection.'}
      />
      </ResizablePanelGroup>

      <ConfirmationDialog
        open={!!collectionToDelete}
        onOpenChange={(open) => !open && setCollectionToDelete(null)}
        title="Delete Collection"
        description={`Are you sure you want to delete "${collectionToDelete?.name}"? This will also delete all data in this collection.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />

      <ConfirmationDialog
        open={!!pendingForceUpdate}
        onOpenChange={(open) => {
          if (!open) {
            setPendingForceUpdate(null);
            setDestructiveWarnings([]);
          }
        }}
        title="Apply destructive schema update?"
        description={
          <div className="space-y-2 text-left">
            <p>This update may break data integrity for existing rows.</p>
            {destructiveWarnings.length > 0 ? (
              <ul className="list-disc pl-4 space-y-1">
                {destructiveWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </div>
        }
        onConfirm={confirmForceUpdate}
        confirmVariant="destructive"
        confirmLabel="Force Update"
      />
    </>
  );
}

